import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TitleStrategy, RouterStateSnapshot } from '@angular/router';
import { Observable, of, takeUntil, Subject } from 'rxjs';
import { AreaService, ProjectService } from '@newroom-connect/library/services';

@Injectable()
export class PageTitleStrategy extends TitleStrategy {
  private readonly projectService = inject(ProjectService);
  private readonly areaService = inject(AreaService);
  private readonly titleService = inject(Title);

  public currentProject = this.projectService.watchCurrentProject();
  public currentArea = this.areaService.currentArea;

  private destroy$ = new Subject<void>();

  /**
   * Update the page title based on the current route.
   *
   * @param routerState The current router state.
   */
  public override updateTitle(routerState: RouterStateSnapshot): void {
    this.getPageTitle(routerState).pipe(takeUntil(this.destroy$)).subscribe(title => {
      this.titleService.setTitle(title);
    });
  }

  /**
   * Build the page title based on the current route.
   *
   * @param routerState The current router state.
   * @returns An observable that emits the page title.
   */
  private getPageTitle(routerState: RouterStateSnapshot): Observable<string> {
    const routeTitle = this.buildTitle(routerState);
    const currentProject = this.currentProject();
    const currentArea = this.currentArea();

    if (!routeTitle || (!currentProject && !currentArea)) {
      return of('');
    }

    const pageTitle = currentProject && Array.isArray(currentProject.translations) && currentProject.translations.length > 0
      ? currentProject.translations[0].title
      : undefined;

    const projectSlug = routerState.url.split('/')[1];
    const urlParts = routerState.url.split('/');

    // Handle area pages specifically
    if (projectSlug && urlParts.length > 3 && urlParts[2] === 'areas') {
      // Use the currentArea from the signal that's already set by the effect
      // This avoids making a new API call since we're observing the AreaService's signal
      if (currentArea && currentProject) {
        return of(`${currentArea.translations[0].title} - ${pageTitle}`);
      }

      // If no current area is available, return the route title
      return of(pageTitle ? `${pageTitle}` : routeTitle);
    }

    // Handle other pages
    const handlers: Record<string, () => Observable<string>> = {
      'signin': () => of(pageTitle ? `${pageTitle} - ${routeTitle}` : routeTitle),
      'legal': () => of(pageTitle ? `${pageTitle} - ${routeTitle}` : routeTitle)
    };

    for (const key in handlers) {
      if (routerState.url.includes(key)) {
        return handlers[key]();
      }
    }

    return of(pageTitle ? `${routeTitle} - ${pageTitle}` : routeTitle);
  }
}
